import React, { useEffect, useState } from "react";
import { LineChart, Line, YAxis, ResponsiveContainer } from "recharts";
import { TrendingUp, TrendingDown, Minus, Sparkles, AlertTriangle, CalendarDays, FolderLock, FileDown } from "lucide-react";
import { toast } from "sonner";
import { boardFetch, boardPackUrl } from "../api";
import { useCompany, Badge, MarkdownLite, ragOf, RAG } from "../components";

const TrendIcon = ({ t }) => t === "up" ? <TrendingUp size={15} className="text-emerald-400" /> : t === "down" ? <TrendingDown size={15} className="text-red-400" /> : <Minus size={15} className="text-white/40" />;

const SPARK = { green: "#34d399", amber: "#fbbf24", red: "#f87171" };

const KpiCard = ({ k }) => (
  <div className="border border-white/10 bg-white/[0.02] p-5" data-testid="kpi-card">
    <div className="flex items-start justify-between">
      <span className="text-[11px] uppercase tracking-wider text-white/45">{k.label}</span>
      <span className={`h-2.5 w-2.5 rounded-full ${k.status === "green" ? "bg-emerald-400" : k.status === "amber" ? "bg-amber-400" : "bg-red-400"}`} />
    </div>
    <div className="mt-3 flex items-baseline gap-2">
      <span className="font-serif text-3xl font-light text-white">{k.value}</span>
      <span className="text-sm text-white/50">{k.unit}</span>
      <span className="ml-auto"><TrendIcon t={k.trend} /></span>
    </div>
    {Array.isArray(k.history) && k.history.length > 1 && (
      <div className="mt-3 h-10" data-testid="kpi-sparkline">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={k.history} margin={{ top: 4, right: 2, bottom: 0, left: 2 }}>
            <YAxis hide domain={["dataMin", "dataMax"]} />
            <Line type="monotone" dataKey="value" stroke={SPARK[k.status] || "#c8a24a"} strokeWidth={2} dot={false} isAnimationActive={false} />
          </LineChart>
        </ResponsiveContainer>
      </div>
    )}
    <div className="mt-2 flex items-center justify-between">
      <span className="text-[11px] uppercase tracking-wider text-champagne/70">{k.category}</span>
      {Array.isArray(k.history) && k.history.length > 1 && (
        <span className="text-[10px] text-white/30">{k.history[0].period}–{k.history[k.history.length - 1].period}</span>
      )}
    </div>
  </div>
);

const Cockpit = () => {
  const { company } = useCompany();
  const [data, setData] = useState(null);
  const [assessment, setAssessment] = useState(company?.last_assessment || null);
  const [aiLoading, setAiLoading] = useState(false);

  useEffect(() => {
    setData(null);
    setAssessment(null);
    boardFetch(`/companies/${company.id}/dashboard`).then(setData).catch((e) => toast.error(e.message));
    boardFetch(`/companies/${company.id}`).then((c) => setAssessment(c.last_assessment || null));
  }, [company.id]);

  const runAi = async () => {
    setAiLoading(true);
    try {
      const res = await boardFetch(`/companies/${company.id}/ai/assessment`, { method: "POST" });
      setAssessment(res.assessment);
      toast.success("Lagebeurteilung erstellt");
    } catch (e) { toast.error(e.message); } finally { setAiLoading(false); }
  };

  if (!data) return <div className="py-20 text-center text-white/40 animate-pulse">Cockpit wird geladen…</div>;

  const sev = data.risks_by_severity || {};
  const comp = data.compliance_by_status || {};

  return (
    <div className="space-y-10" data-testid="cockpit">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <span className="text-[11px] uppercase tracking-[0.3em] text-champagne">Gesamtlage</span>
          <h1 className="mt-1 font-serif text-4xl font-light text-white">{company.name}</h1>
          <p className="mt-2 text-sm text-white/45">{company.sector} · {company.legal_form} · seit {company.since || "—"} · {company.employees ? `${company.employees} MA` : ""}</p>
        </div>
        <a href={boardPackUrl(company.id)} target="_blank" rel="noopener noreferrer" data-testid="board-pack-download"
          className="inline-flex items-center gap-2 self-start border border-champagne/40 px-5 py-2.5 text-xs uppercase tracking-widest text-champagne transition-colors hover:bg-champagne/10 sm:self-auto">
          <FileDown size={15} /> Board-Pack (PDF)
        </a>
      </div>

      {/* KPIs */}
      <div>
        <h2 className="mb-4 text-xs uppercase tracking-[0.25em] text-white/45">Kennzahlen</h2>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {data.kpis.map((k) => <KpiCard key={k.id} k={k} />)}
        </div>
      </div>

      {/* Summary row */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <div className="border border-white/10 p-6">
          <div className="flex items-center gap-2 text-white/60"><AlertTriangle size={16} className="text-champagne" /><span className="text-xs uppercase tracking-widest">Risiken</span></div>
          <p className="mt-3 font-serif text-4xl font-light text-white">{data.risks_total}</p>
          <div className="mt-3 flex gap-2">
            {["high", "medium", "low"].map((s) => sev[s] ? <Badge key={s} value={`${sev[s]} ${s}`} tone={RAG[s]} /> : null)}
          </div>
        </div>
        <div className="border border-white/10 p-6">
          <div className="flex items-center gap-2 text-white/60"><CalendarDays size={16} className="text-champagne" /><span className="text-xs uppercase tracking-widest">Offene Feststellungen</span></div>
          <p className="mt-3 font-serif text-4xl font-light text-white">{data.open_findings_count}</p>
          <p className="mt-3 text-xs text-white/40">{data.upcoming_meetings.length} anstehende Sitzung(en)</p>
        </div>
        <div className="border border-white/10 p-6">
          <div className="flex items-center gap-2 text-white/60"><FolderLock size={16} className="text-champagne" /><span className="text-xs uppercase tracking-widest">Compliance / IKS</span></div>
          <div className="mt-3 flex gap-2">
            {["green", "amber", "red"].map((s) => comp[s] ? <Badge key={s} value={`${comp[s]}`} tone={RAG[s]} /> : null)}
          </div>
          <p className="mt-3 text-xs text-white/40">{data.documents_count} Dokument(e)</p>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-8 lg:grid-cols-2">
        {/* Top risks */}
        <div>
          <h2 className="mb-4 text-xs uppercase tracking-[0.25em] text-white/45">Wesentliche Risiken</h2>
          <div className="space-y-3">
            {data.top_risks.map((r) => (
              <div key={r.id} className="flex items-start justify-between border border-white/10 p-4">
                <div className="pr-4"><p className="text-sm text-white/85">{r.title}</p><p className="mt-1 text-xs text-white/40">{r.category} · {r.owner}</p></div>
                <Badge value={r.severity} tone={ragOf(null, r.severity)} />
              </div>
            ))}
            {data.top_risks.length === 0 && <p className="text-sm text-white/35">Keine Risiken erfasst</p>}
          </div>
        </div>
        {/* Open findings */}
        <div>
          <h2 className="mb-4 text-xs uppercase tracking-[0.25em] text-white/45">Offene Audit-Feststellungen</h2>
          <div className="space-y-3">
            {data.open_findings.map((f) => (
              <div key={f.id} className="flex items-start justify-between border border-white/10 p-4">
                <div className="pr-4"><p className="text-sm text-white/85">{f.title}</p><p className="mt-1 text-xs text-white/40">{f.owner} · fällig {f.due_date}</p></div>
                <Badge value={f.severity} tone={ragOf(null, f.severity)} />
              </div>
            ))}
            {data.open_findings.length === 0 && <p className="text-sm text-white/35">Keine offenen Feststellungen</p>}
          </div>
        </div>
      </div>

      {/* Upcoming meetings */}
      <div>
        <h2 className="mb-4 text-xs uppercase tracking-[0.25em] text-white/45">Anstehende Sitzungen</h2>
        <div className="space-y-3">
          {data.upcoming_meetings.map((m) => (
            <div key={m.id} className="border border-white/10 p-4">
              <div className="flex items-center justify-between"><p className="text-sm text-white/85">{m.title}</p><span className="text-xs text-champagne">{m.date}</span></div>
              <p className="mt-1 text-xs text-white/40">{m.location} · {(m.agenda || []).length} Traktanden</p>
            </div>
          ))}
          {data.upcoming_meetings.length === 0 && <p className="text-sm text-white/35">Keine anstehenden Sitzungen</p>}
        </div>
      </div>

      {/* AI assessment */}
      <div className="border border-champagne/25 bg-champagne/[0.03] p-8">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-champagne"><Sparkles size={18} /><h2 className="font-serif text-2xl font-light text-white">KI-Lagebeurteilung</h2></div>
          <button onClick={runAi} disabled={aiLoading} data-testid="ai-assessment-btn"
            className="bg-champagne px-5 py-2.5 text-xs uppercase tracking-widest text-obsidian hover:bg-champagne-light disabled:opacity-50">
            {aiLoading ? "Analyse läuft…" : assessment ? "Neu generieren" : "Lagebeurteilung erstellen"}
          </button>
        </div>
        <div className="mt-6 text-white/75" data-testid="ai-assessment-output">
          {aiLoading && <p className="animate-pulse text-white/40">Die KI wertet KPIs, Risiken, Feststellungen und Compliance aus…</p>}
          {!aiLoading && assessment && <MarkdownLite text={assessment} />}
          {!aiLoading && !assessment && <p className="text-sm text-white/40">Erstellen Sie eine KI-gestützte Gesamteinschätzung auf Basis der erfassten Daten.</p>}
        </div>
      </div>
    </div>
  );
};

export default Cockpit;
